import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import TrendChart from "../../../components/Dashboard/TrendChart/TrendChart";
import { TREND_CHART_CONFIG } from "../../../constants/customers";
import TrendChartLegend from "components/Dashboard/TrendChart/TrendChartLegend/TrendChartLegend";
import PnlChartPopup from "components/Dashboard/TrendChart/TrendChartContent/PnlChartPopup/PnlChartPopup";
import useContainerDimensions from "utils/useContainerDimensions";
import LotInfoBar from "components/CustomGridItems/LotInfoBar/LotInfoBar";
import {
  BREAKPOINT_DESKTOP_XL,
  BREAKPOINT_MOBILE,
  CARD_MARGINS,
} from "constants/constants";
import CardWithControls from "components/Dashboard/CardWithControls/CardWithControls";
import { PageNameEnum } from "types/enums";
import { LOT_INFO_BAR_HEIGHT } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { CARD_TOPBAR_HEIGHT } from "components/Dashboard/CardWithControls/cardWithControlsUtils";
import ServiceOutageIndicator from "components/SimpleComponents/TopBanner/ServiceOutageIndicator";
import styled, {
  FlattenSimpleInterpolation,
  css,
} from "styled-components/macro";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { getScaleFromWidthForLargeScreens } from "pages/DashboardPages/DashboardPagesContent/dashboardPagesContentUtils";
import { getTrendChartResponsiveStyles } from "components/Dashboard/styles/getDashboardCardsResponsiveStyles";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

export function GridLayoutTrendChart({
  trendChartWidth,
}: {
  trendChartWidth: number;
}) {
  const ckey = useCustomerKey();
  const [dimensionsRef, pnlPopupWrapperDimensions] = useContainerDimensions();
  const [
    {
      general: { enableLotInfoModule },
    },
  ] = useFlightConfig();
  const { translate } = useFlightTranslation();
  const scale = getScaleFromWidthForLargeScreens(trendChartWidth);
  return (
    <GridLayoutTrendChartStyles
      $customCss={css`
        height: calc(
          100% -
            ${enableLotInfoModule
              ? LOT_INFO_BAR_HEIGHT +
                CARD_TOPBAR_HEIGHT +
                CARD_MARGINS[PageNameEnum.gridLayout] * 2
              : 0}px
        );
      `}
      ref={dimensionsRef}
      {...{
        $scale: scale,
        $enableLotInfoModule: enableLotInfoModule,
        $width: trendChartWidth,
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <ServiceOutageIndicator
          customCss={css`
            margin: 0 5px;
            flex-shrink: 0.1;
            @media (min-width: ${BREAKPOINT_MOBILE}px) {
              margin: 5px 5px 10px;
            }
            position: fixed;
            z-index: 1000;
            bottom: 8px;
            opacity: 0.9;
            box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
            left: 0;
            right: 0;
          `}
        />
        {enableLotInfoModule ? (
          <CardWithControls
            title={translate("Lot_Progression")}
            isFullHeight={false}
            showCardBorder={true}
            showTitleBorder={false}
            customCss={css`
              [class*="CardTopbarStyles"] {
                background: rgba(0, 0, 0, 0.1);
              }
            `}
          >
            <LotInfoBar />
          </CardWithControls>
        ) : null}
        <CardWithControls
          title={translate("Trend_Chart")}
          customCss={css`
            display: flex;
            flex-direction: column;
            .pnlChartPopupContent {
              height: 100%;
            }
          `}
        >
          <div
            className="trendChartAndModalWrapper"
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexGrow: 1,
              flexDirection: "column",
            }}
          >
            <TrendChart />
            <PnlChartPopup
              portalContainerSelector="trendChartAndModalWrapper"
              containerWidth={pnlPopupWrapperDimensions.width}
            />
            {TREND_CHART_CONFIG[ckey]?.categories && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TrendChartLegend scale={0.5} />
              </div>
            )}
          </div>
        </CardWithControls>
      </div>
    </GridLayoutTrendChartStyles>
  );
}
const GridLayoutTrendChartStyles = styled.div<{
  $customCss: string | FlattenSimpleInterpolation;
  $scale: number;
  $enableLotInfoModule: boolean;
  $width: number;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${props => props.$width}px;
  height: calc(
    100% -
      ${props =>
        props.$enableLotInfoModule
          ? LOT_INFO_BAR_HEIGHT +
            CARD_TOPBAR_HEIGHT +
            CARD_MARGINS[PageNameEnum.gridLayout] * 2
          : 0}px
  );
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    ${props => getTrendChartResponsiveStyles(props.$scale * 1.75)}
    [class*="TrendChartLoaderSkeleton__TrendChartLoaderSkeletonStyles"] {
      transform-origin: top center;
      margin: auto;
    }
  }
  ${props => props.$customCss || ""}
`;
